import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

/** Material UI Packages. */
import { Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Material UI Icons. */
import Groups2Icon from '@mui/icons-material/Groups2';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { callAPI } from 'shared/api/callApi';
import { PageHeader } from 'shared/ui/HeaderIcon';
import CircularLoading from 'shared/ui/CircularLoading';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';

import EmployeeSetting from './components/EmployeeSetting';
import ResultCard from './components/ResultCard';
import ResultChart from './components/ResultChart';

/**
 * Custom hook to manage performance data fetching.
 * @param {Function} getAccessTokenSilently - Auth0 token getter.
 * @param {string} userEmail - Current user's email.
 * @returns {Object} Performance data and loading states.
 */
const usePerformanceData = (getAccessTokenSilently, userEmail) => {
    /** Array of employee names for selection. */
    const [employeeName, setEmployeeName] = useState([]);
    /** Loading state for initial user data fetch. */
    const [isUserLoading, setIsUserLoading] = useState(false);

    /** Fetch employee data on component mount. */
    const fetchEmployeeData = useCallback(() => {
        callAPI({
            method: 'get',
            module: 'performance',
            getAccessTokenSilently,
            email: userEmail,
            onSuccess: (resp) => {
                setEmployeeName(resp.data);
            },
            onFinal: () => {
                setIsUserLoading(true);
            },
        });
    }, [getAccessTokenSilently, userEmail]);

    useEffect(() => {
        fetchEmployeeData();
    }, [fetchEmployeeData]);

    return {
        employeeName,
        isUserLoading,
    };
};

/**
 * Main Performance component that displays employee performance metrics
 * @returns {JSX.Element} Performance page component
 */
export default function Performance() {
    /** Destructuring values from useAuth0 hook */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Common performance results data */
    const [commonResult, setCommonResult] = useState({});
    /** Personal performance results data */
    const [personalResult, setPersonalResult] = useState({});
    /** Labor chart data array */
    const [laborChartData, setLaborChartData] = useState([]);
    /** Labor chart label array */
    const [laborChartLabel, setLaborChartLabel] = useState([]);
    /** Loading state for search operations */
    const [searchLoading, setSearchLoading] = useState(false);

    /** Custom hook for employee data management */
    const { employeeName, isUserLoading } = usePerformanceData(getAccessTokenSilently, user.email);

    /** Set document title on component mount */
    useEffect(() => {
        document.title = 'Performance | Operation System';
    }, []);

    /** Render loading skeleton while user data is being fetched */
    if (!isUserLoading) {
        return (
            <Paper sx={customized_style.itemPaper}>
                <CustomSkeleton2 />
            </Paper>
        );
    }

    return (
        <Paper sx={customized_style.itemPaper}>
            <PageHeader title={'PERFORMANCE'} icon={Groups2Icon} />
            <CircularLoading loading={searchLoading} />
            <Grid container spacing={2}>
                <Grid size={5} sx={{ height: '80vh' }}>
                    <EmployeeSetting
                        setSearchLoading={setSearchLoading}
                        employeeName={employeeName}
                        setCommonResult={setCommonResult}
                        setPersonalResult={setPersonalResult}
                        setLaborChartData={setLaborChartData}
                        setLaborChartLabel={setLaborChartLabel}
                    />
                    <ResultCard commonResult={commonResult} personalResult={personalResult} />
                </Grid>
                <Grid size={7} sx={{ height: '80vh' }}>
                    <ResultChart laborChartData={laborChartData} laborChartLabel={laborChartLabel} />
                </Grid>
            </Grid>
        </Paper>
    );
}
